import { trackAddShippingInfoEvent } from 'src/app/@utils/gtm-tracking/events/trackAddShippingInfoEvent';
import { trackReserveInStoreEvent } from 'src/app/@utils/gtm-tracking/events/trackReserveInStoreEvent';
import { trackUserSignInEvent } from 'src/app/@utils/gtm-tracking/events/trackUserSignInEvent';

import {
    trackAddPaymentInfoEvent,
    trackAddToBasketEvent,
    trackCheckoutBeginEvent,
    trackDropdownSearchEvent,
    trackingStarted,
    trackPageViewEvent,
    trackProductClickEvent,
    trackProductDetailImpressionEvent,
    trackProductImpressionEvent,
    trackPromotionClickEvent,
    trackPromotionViewEvent,
    trackPurchaseEvent,
    trackRefundEvent,
    trackRemoveFromBasketEvent,
    trackSubmittedSearchEvent,
    trackViewBasketEvent
} from '../events';
import { GTMWindow, LoadStatus, TrackableEvent, TrackingConfig, TrackingObject } from '../model';
import { isBrowser, loadScript } from '../utils';
import { pushEventFactory } from './pushEventFactory';
import { PageViewData } from '@utils/gtm-tracking/events/trackPageViewEvent';
import { ProductSearchViewModel } from '@models/api-search.models';
import { PromotionSettings } from '@utils/gtm-tracking/model/Promotion';

const getRejectionReason = (windowReference: Window | undefined, trackingEnabled: boolean, gtmId: string) => {
    if (!isBrowser(windowReference) || !windowReference) {
        return 'Window document not available';
    }
    if (!trackingEnabled) {
        return 'Tracking is not enabled';
    }
    if (!gtmId) {
        return 'No container id was specified for google tag manager. Script load aborted.';
    }
    if (!(windowReference as unknown as GTMWindow).dataLayer) {
        return 'GTM dataLayer is not defined. Please ensure that it has been defined in the <head> section.';
    }
    return undefined;
};

const noop = () => undefined as never;

const configurationDefaults: Partial<TrackingConfig> = {
    trackingTimeoutMs: 2000,
    overrideEvent: {
        trackPage: noop,
        trackAddToBasket: noop,
        trackRemoveFromBasket: noop,
        trackPurchase: noop,
        trackProductImpression: noop,
        trackProductDetailImpression: noop
    }
};

export const configureTracking = (trackingConfiguration: TrackingConfig): TrackingObject => {
    const config = { ...configurationDefaults, ...trackingConfiguration };
    const { gtmId, trackingEnabled, overrideEvent, globalParamExtractor } = config;

    const isServer = !isBrowser(config.window) || !config.window;
    const windowReference: GTMWindow = isServer
        ? {
              google_tag_manager: undefined,
              dataLayer: [],
              document: {
                  title: 'unknown',
                  location: {
                      href: 'unknown',
                      pathname: 'unknown'
                  }
              }
          }
        : (config.window as unknown as GTMWindow);

    let loadComplete: Promise<LoadStatus>;
    let trackStart = false;

    const rejectionReason = getRejectionReason(config.window, trackingEnabled, gtmId);

    if (rejectionReason) {
        loadComplete = new Promise(resolve => resolve({ status: 'ERROR', rejectionReason }));
    } else {
        let loadResolve: (status: LoadStatus) => void;
        loadComplete = new Promise(resolve => {
            loadResolve = resolve;
        });

        const { loadPromise, alreadyExists } = loadScript(
            config.window as Window,
            `https://www.googletagmanager.com/gtm.js?id=${gtmId}`,
            config.debugMode ?? false
        );

        trackStart = !alreadyExists;

        loadPromise
            .then(() => {
                if (!windowReference.google_tag_manager) {
                    loadResolve({
                        status: 'ERROR',
                        rejectionReason: 'Script load was blocked'
                    });
                    return;
                }
                loadResolve({
                    status: 'READY'
                });
            })
            .catch(() => {
                loadResolve({
                    status: 'ERROR',
                    rejectionReason: 'Script load was blocked'
                });
            });
    }

    const pushEvent = pushEventFactory(windowReference, config, loadComplete, globalParamExtractor);

    if (trackStart) {
        loadComplete.then(() => pushEvent(trackingStarted()));
    }

    return {
        loadComplete,
        trackPage: (data: PageViewData) =>
            pushEvent({
                ...trackPageViewEvent(windowReference, data),
                ...overrideEvent?.trackPage?.()
            }),
        trackProductClick: (products, impressionSource) =>
            pushEvent({
                ...trackProductClickEvent(products, impressionSource)
            }),
        trackAddToBasket: products =>
            pushEvent({
                ...trackAddToBasketEvent(products),
                ...overrideEvent?.trackAddToBasket?.()
            }),
        trackViewBasket: (products, basketValue) =>
            pushEvent({
                ...trackViewBasketEvent(products, basketValue),
                ...overrideEvent?.trackViewBasket?.()
            }),
        trackRemoveFromBasket: products =>
            pushEvent({
                ...trackRemoveFromBasketEvent(products),
                ...overrideEvent?.trackRemoveFromBasket?.()
            }),
        trackPromotionView: (promoSetting: PromotionSettings, product?: ProductSearchViewModel) =>
            pushEvent({
                ...trackPromotionViewEvent(promoSetting, product)
            }),
        trackRefund: transactionId =>
            pushEvent({
                ...trackRefundEvent(transactionId)
            }),
        trackPromotionClick: (promoSetting, product) =>
            pushEvent({
                ...trackPromotionClickEvent(promoSetting, product)
            }),
        trackCheckoutBegin: (products, basketValue, coupon) =>
            pushEvent({
                ...trackCheckoutBeginEvent(products, basketValue, coupon)
            }),
        trackAddShippingInfo: (products, basketValue, coupon, shippingProvider, shippingTier, store) =>
            pushEvent({
                ...trackAddShippingInfoEvent(products, basketValue, coupon, shippingProvider, shippingTier, store)
            }),
        trackAddPaymentInfo: (products, basketValue, paymentMethod) =>
            pushEvent({
                ...trackAddPaymentInfoEvent(products, basketValue, paymentMethod)
            }),
        trackUserSignIn: (userId, verified) =>
            pushEvent({
                ...trackUserSignInEvent(userId, verified)
            }),
        trackReserveInStore: (store, transactionId, total, items) =>
            pushEvent({
                ...trackReserveInStoreEvent(store, transactionId, total, items)
            }),
        trackPurchase: (
            orderId,
            products,
            totalPrice,
            discountValue,
            vatPrice,
            coupon,
            shippingPrice,
            currency,
            affiliation
        ) =>
            pushEvent({
                ...trackPurchaseEvent(
                    orderId,
                    products,
                    totalPrice,
                    discountValue,
                    vatPrice,
                    coupon,
                    shippingPrice,
                    currency,
                    affiliation
                ),
                ...overrideEvent?.trackPurchase?.()
            }),
        trackProductImpression: (products, impressionSource) =>
            pushEvent({
                ...trackProductImpressionEvent(products, impressionSource),
                ...overrideEvent?.trackProductImpression?.()
            }),
        trackProductDetailImpression: product =>
            pushEvent({
                ...trackProductDetailImpressionEvent(product),
                ...overrideEvent?.trackProductDetailImpression?.()
            }),
        trackSubmittedSearch: query =>
            pushEvent({
                ...trackSubmittedSearchEvent(query)
            }),
        trackDropdownSearch: (query, productName, index, hits) =>
            pushEvent({
                ...trackDropdownSearchEvent(query, productName, index, hits)
            }),
        trackCustomEvent: (event: unknown) => pushEvent(event as TrackableEvent)
    };
};

export const configureBotTracking: TrackingObject = {
    loadComplete: Promise.resolve({ status: 'ERROR', rejectionReason: 'Bot detected' }),
    trackCustomEvent: event => Promise.resolve({ success: false, message: `Bot detected ${event}` }),
    trackPage: page => Promise.resolve({ success: false, message: `Bot detected ${page}` }),
    trackProductImpression: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackProductDetailImpression: product => Promise.resolve({ success: false, message: `Bot detected ${product}` }),
    trackProductClick: product => Promise.resolve({ success: false, message: `Bot detected ${product}` }),
    trackAddToBasket: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackRemoveFromBasket: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackViewBasket: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackCheckoutBegin: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackAddShippingInfo: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackAddPaymentInfo: products => Promise.resolve({ success: false, message: `Bot detected ${products}` }),
    trackUserSignIn: userId => Promise.resolve({ success: false, message: `Bot detected ${userId}` }),
    trackReserveInStore: store => Promise.resolve({ success: false, message: `Bot detected ${store}` }),
    trackPurchase: orderId => Promise.resolve({ success: false, message: `Bot detected ${orderId}` }),
    trackRefund: transactionId => Promise.resolve({ success: false, message: `Bot detected ${transactionId}` }),
    trackPromotionView: (promoSetting) => Promise.resolve({ success: false, message: `Bot detected ${promoSetting}` }),
    trackPromotionClick: product => Promise.resolve({ success: false, message: `Bot detected ${product}` }),
    trackSubmittedSearch: query => Promise.resolve({ success: false, message: `Bot detected ${query}` }),
    trackDropdownSearch: query => Promise.resolve({ success: false, message: `Bot detected ${query}` })
};
